import createClient from 'openapi-fetch';
import type { paths } from './api-types.gen.js';

const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:3001';

export const api = createClient<paths>({
  baseUrl: BASE_URL,
  credentials: 'include',
});

let accessToken: string | null = null;
export function setAccessToken(token: string | null): void {
  accessToken = token;
  // A fresh token means a fresh session, so a previously-rejected refresh cookie
  // is no longer the last word — see `refreshRejected`.
  if (token !== null) refreshRejected = false;
}
export function getAccessToken(): string | null {
  return accessToken;
}

api.use({
  onRequest({ request }) {
    if (accessToken) request.headers.set('authorization', `Bearer ${accessToken}`);
    return request;
  },
});

interface RefreshSuccess {
  accessToken: string;
  user: {
    id: string;
    email: string;
    role: 'admin' | 'editor' | 'viewer';
    organizationId: string;
    name: string | null;
    firstName: string | null;
    lastName: string | null;
  };
}

/**
 * Why a refresh did not produce a session.
 *
 * - `rate_limited` — the refresh endpoint itself answered 429. The cookie is
 *   probably fine; the caller must surface a rate limit and leave the session
 *   alone. Treating this as an auth failure (as it once did) silently logged
 *   people out the moment the throttle bit.
 * - `failed` — the cookie is missing/expired/revoked, the server erred, or the
 *   network call blew up.
 */
export type RefreshFailureReason = 'rate_limited' | 'failed';

/**
 * Marks a failure as client-constructed.
 *
 * The discriminant deliberately is NOT a normal property. `RefreshSuccess` is a
 * cast of `res.json()` — whatever the server sends — so any string key we branch
 * on is a key the server could one day introduce. It used to be `ok`, and the
 * day `/auth/refresh` started returning an `ok` field (an innocuous API change,
 * and one nothing would flag) every successful refresh would have been read as a
 * failure with `reason: undefined`, which is not `'rate_limited'`, so the
 * bootstrap would have called `clear()`: every page load logging the user out.
 *
 * `JSON.parse` cannot produce a symbol-keyed property, so the shape of the
 * server's response can no longer reach this branch at all. That is the whole
 * point — discriminating on `accessToken` instead would only have moved the
 * coupling to a different field name.
 */
const REFRESH_FAILURE: unique symbol = Symbol('refresh-failure');

export interface RefreshFailure {
  readonly [REFRESH_FAILURE]: true;
  reason: RefreshFailureReason;
  /** From `Retry-After`, when the server sent a readable one. */
  retryAfterSeconds: number | null;
}

export type RefreshOutcome = RefreshSuccess | RefreshFailure;

export function isRefreshFailure(outcome: RefreshOutcome): outcome is RefreshFailure {
  return REFRESH_FAILURE in outcome;
}

/**
 * The only way to build a {@link RefreshFailure}. Exported so tests construct
 * the same value production does rather than hand-rolling a literal that could
 * drift from it.
 */
export function refreshFailure(
  reason: RefreshFailureReason,
  retryAfterSeconds: number | null = null,
): RefreshFailure {
  return { [REFRESH_FAILURE]: true, reason, retryAfterSeconds };
}

/**
 * `Retry-After` in delta-seconds. The refresh route sits behind two named
 * throttler buckets, and @nestjs/throttler suffixes the header with the bucket
 * name, so check those too. Returns null when absent or unreadable — in
 * cross-origin dev CORS hides it, which is fine: it only sharpens the message.
 */
function retryAfterOf(res: Response): number | null {
  for (const name of ['Retry-After', 'Retry-After-auth-refresh', 'Retry-After-auth-refresh-ip']) {
    const raw = res.headers.get(name);
    if (raw === null) continue;
    const trimmed = raw.trim();
    if (!/^\d+$/.test(trimmed)) continue;
    const n = Number(trimmed);
    if (Number.isSafeInteger(n)) return n;
  }
  return null;
}

/** In-flight refresh, shared by every caller that arrives while it runs. */
let refreshingPromise: Promise<RefreshOutcome> | null = null;

/**
 * Latch: the server has told us this refresh cookie is no good. Without it a
 * page holding a dozen queries answers each 401 with its own refresh attempt as
 * the requests trickle in past the in-flight window — a 401 → refresh → 401 loop
 * that spends the refresh quota and delays the redirect to /login. Cleared by
 * {@link setAccessToken} when a real session arrives.
 */
let refreshRejected = false;

export async function refreshOnce(): Promise<RefreshOutcome> {
  if (refreshRejected) return refreshFailure('failed');
  refreshingPromise ??= (async () => {
    try {
      const res = await fetch(`${BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.status === 429) return refreshFailure('rate_limited', retryAfterOf(res));
      if (!res.ok) {
        // Latch ONLY on an authentication verdict. 401/403 mean the server has
        // looked at this cookie and rejected it — asking again cannot change
        // that. A 502/503 during a deploy means nothing about the cookie, and
        // latching on it used to disable refresh for the rest of the tab's life,
        // so a user mid-deploy was stuck until they reloaded by hand.
        if (res.status === 401 || res.status === 403) refreshRejected = true;
        return refreshFailure('failed');
      }
      const data = (await res.json()) as RefreshSuccess;
      setAccessToken(data.accessToken);
      return data;
    } catch {
      // Network/parse error — no verdict, so don't latch; the next attempt may
      // well succeed once connectivity is back.
      return refreshFailure('failed');
    } finally {
      setTimeout(() => {
        refreshingPromise = null;
      }, 0);
    }
  })();
  return refreshingPromise;
}
